// Read a sheet on the canvas, with everything it is connected to beside it.
//
// Doc Control could show you a row and hand you a download. But a
// drawing is not read alone - the whole reason a set is a set is that
// its sheets point at each other, and the one thing a person cannot do
// while holding a sheet is find out WHO POINTS AT IT. That reverse
// direction is invisible on paper and it is the most useful thing here.
//
// Three kinds of relation, kept apart because they mean different
// things (see routes/doc-control/:id/related):
//
//   Refers to     printed callouts on this sheet - "SEE DETAIL 3/1-A2.1"
//   Referred by   sheets that call out THIS one - the reverse direction
//   Shares tags   the same equipment appearing elsewhere. Not a printed
//                 reference; a content relationship, ranked by how many
//                 tags are in common
//
// Clicking any of them opens that sheet on the same canvas and asks the
// same question again, so a set can be walked rather than searched.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, Spin, Tag, Tooltip, Empty, Button, message } from "antd";
import {
  LinkOutlined, RollbackOutlined, TagsOutlined, ApartmentOutlined,
  WarningOutlined, BlockOutlined,
} from "@ant-design/icons";
import IssueDocumentPane from "pages/Issue/components/IssueDocumentPane";
import { url, tokenRef } from "api";
import { absoluteDocumentUrl } from "helpers/pdfPreview";
import { useWheelZoom } from "helpers/wheelZoom";
import SheetGridOverlay from "components/Sheets/SheetGridOverlay";
import useSheetGrid from "components/Sheets/useSheetGrid";
import { SHEET_GRID_WHY } from "api/glance/sheetGrid";

const PANEL_W = 340;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

const IS_PDF = /\.pdf(\?|$)/i;
const IS_IMAGE = /\.(png|jpe?g|gif|webp|bmp|tiff?)(\?|$)/i;

/**
 * The rendered pages of a sheet, as a canvas.
 *
 * Every document filed here is rasterised on upload (utils/sheetPages),
 * so the pages are already images and there is nothing to parse. That
 * matters beyond speed: Doc Control holds PNG markup mirrored off issues
 * as well as drawings, and handing one of those to pdf.js - which the
 * first version of this did, by telling the pane every file was a PDF -
 * gets "Invalid PDF structure" over a picture that was fine.
 */
// G, or the Grid button: the building grid over the page, as G is in the 3D
// views (components/Sheets/SheetGridOverlay). The choice is kept across pages
// and sheets in this drawer; a page that cannot show it says why.
function PageCanvas({ pages, name, projectId, docId }) {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [gridOn, setGridOn] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { setI(0); setZoom(1); }, [pages]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key === "g" || e.key === "G" || e.code === "KeyG") setGridOn((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const page = Math.min(i, Math.max(0, (pages || []).length - 1)) + 1;
  const grid = useSheetGrid(projectId, docId, page, gridOn);
  // Same wheel behaviour as every other canvas (helpers/wheelZoom).
  useWheelZoom(scrollRef, (factor) => {
    setZoom((z) => Math.max(0.25, Math.min(6, z * factor)));
  }, []);
  const list = pages || [];
  if (!list.length) return null;
  const src = absoluteDocumentUrl(list[Math.min(i, list.length - 1)]);
  return (
    <div style={{
      flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
      background: "#1A1A1A", border: "1px solid #0C0C0C", borderRadius: 6, overflow: "hidden",
    }}>
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", justifyContent: "center" }}>
        <div style={{ position: "relative", width: `${zoom * 100}%`, alignSelf: "flex-start" }}>
          <img
            src={src}
            alt={name || "sheet"}
            style={{ display: "block", width: "100%", height: "auto" }}
          />
          {gridOn && <SheetGridOverlay lines={grid.lines} />}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, background: "#15181B" }}>
        <Button size="small" disabled={i <= 0} onClick={() => setI((v) => v - 1)}>Prev</Button>
        <span style={{ color: "#D7DDE2", fontSize: 12 }}>
          page {Math.min(i, list.length - 1) + 1} / {list.length}
        </span>
        <Button size="small" disabled={i >= list.length - 1} onClick={() => setI((v) => v + 1)}>Next</Button>
        <Tooltip title={gridOn && !grid.loading && !grid.lines.length ? (SHEET_GRID_WHY[grid.reason] || SHEET_GRID_WHY.unavailable) : "Building grid (G)"}>
          <Button size="small" type={gridOn ? "primary" : "default"} aria-pressed={gridOn} onClick={() => setGridOn((v) => !v)}>Grid</Button>
        </Tooltip>
        {gridOn && !grid.loading && !grid.lines.length && (
          <span data-testid="sheet-grid-why" style={{ color: "#F0B35A", fontSize: 12 }}>{SHEET_GRID_WHY[grid.reason] || SHEET_GRID_WHY.unavailable}</span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <Button size="small" onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}>-</Button>
          <span style={{ color: "#D7DDE2", fontSize: 12, minWidth: 46, textAlign: "center" }}>
            {Math.round(zoom * 100)}%
          </span>
          <Button size="small" onClick={() => setZoom((z) => Math.min(5, z + 0.25))}>+</Button>
        </span>
      </div>
    </div>
  );
}

// A row in the side menu. Deliberately shows WHY it is related - the
// detail number, or which tags are shared - because "related" without a
// reason is just a longer list.
function RelatedRow({ item, onOpen, reason }) {
  const openable = !!(item.docId && item.link);
  return (
    <button
      type="button"
      disabled={!openable}
      onClick={() => openable && onOpen(item)}
      title={openable ? "Open this sheet on the canvas" : "This sheet is not in the register"}
      style={{
        display: "block", width: "100%", textAlign: "left", marginBottom: 6,
        border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 9px",
        background: openable ? "#fff" : "#fafafa",
        cursor: openable ? "pointer" : "default",
        opacity: openable ? 1 : 0.7,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {item.sheetNumber || "(no number)"}
        </span>
        {!openable && (
          <Tooltip title="Called out by this set but never delivered">
            <WarningOutlined style={{ color: "#d97706", fontSize: 12 }} />
          </Tooltip>
        )}
        {item.discipline && (
          <span style={{ marginLeft: "auto", fontSize: 10, color: "#6b7280" }}>{item.discipline}</span>
        )}
      </div>
      {item.sheetName && (
        <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2, lineHeight: 1.3 }}>
          {item.sheetName.length > 64 ? `${item.sheetName.slice(0, 64)}…` : item.sheetName}
        </div>
      )}
      {reason(item)}
    </button>
  );
}

function Group({ icon, title, hint, items, onOpen, reason, empty }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {icon}
        <span style={{ fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {title}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280" }}>{items.length}</span>
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>{hint}</div>
      {items.length
        ? items.map((it, i) => (
          <RelatedRow key={`${it.docId || it.sheetNumber}-${i}`} item={it} onOpen={onOpen} reason={reason} />
        ))
        : <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>{empty}</div>}
    </div>
  );
}

export default function SheetCanvasDrawer({ doc, open, onClose }) {
  // The sheet currently ON the canvas - not necessarily the one the eye
  // was clicked on, because following a link changes it.
  const [current, setCurrent] = useState(null);
  const [related, setRelated] = useState(null);
  const [busy, setBusy] = useState(false);
  // Where we came from, so following a callout is reversible.
  const [trail, setTrail] = useState([]);

  // Which sheet the canvas is being asked for, so a slow answer for the
  // one we have just navigated away from cannot overwrite the new one.
  const loadSeq = useRef(0);

  const load = useCallback(async (d) => {
    const seq = ++loadSeq.current;
    setCurrent(d);
    setBusy(true);
    setRelated(null);

    // ASK for the rendered pages; do not believe the row.
    //
    // A row handed over by the list carries DocControl.pages, which is
    // filled in by the upload path - so it is empty for every sheet
    // filed before that path existed, which is all of them. The canvas
    // then fell through to the PDF pane for everything, and the pane
    // cannot draw a PNG: an image sheet opened as "No viewer for this
    // format - open it in a new tab", and a drawing that had already
    // been rasterised on the server was parsed again in the browser.
    //
    // /doc-control/:id/pages looks on disk for pages the row never
    // recorded and persists what it finds, so this is also the repair.
    fetch(`${url}/doc-control/${d._id}/pages`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const pages = body && Array.isArray(body.pages) ? body.pages : [];
        if (!pages.length || seq !== loadSeq.current) return;
        setCurrent((cur) => (cur && String(cur._id) === String(d._id) ? { ...cur, pages } : cur));
      })
      .catch(() => { /* the PDF pane is still there to fall back to */ });

    try {
      const res = await fetch(`${url}/doc-control/${d._id}/related`, { headers: auth() });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      if (seq !== loadSeq.current) return;
      setRelated(body);
    } catch (e) {
      message.error(e.message || "Could not read the related sheets");
      setRelated({ refersTo: [], referredBy: [], sharesTags: [], elements: [], counts: {} });
    } finally {
      if (seq === loadSeq.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (open && doc) { setTrail([]); load(doc); }
    if (!open) { setCurrent(null); setRelated(null); setTrail([]); }
  }, [open, doc, load]);

  const follow = (item) => {
    if (current) setTrail((t) => [...t, current]);
    load({ _id: item.docId, sheetName: item.sheetName, sheetNumber: item.sheetNumber, link: item.link, pages: item.pages });
  };
  const back = () => {
    setTrail((t) => {
      const prev = t[t.length - 1];
      if (prev) load(prev);
      return t.slice(0, -1);
    });
  };

  // What this row actually IS, rather than what we wish it were. The
  // rendered pages are preferred over the source file for everything:
  // they are already images, so a 70 MB drawing opens as fast as a
  // screenshot does.
  const link = current && current.link ? String(current.link) : "";
  const pages = (current && current.pages) || [];
  const kind = IS_PDF.test(link) ? "pdf" : IS_IMAGE.test(link) ? "image" : "other";
  const docsForPane = link
    ? [{ url: link, name: current.sheetName || current.sheetNumber || "Sheet", kind }]
    : [];

  const calloutReason = (it) => (it.details && it.details.length
    ? <div style={{ marginTop: 4 }}>
      {it.details.slice(0, 6).map((d) => <Tag key={d} style={{ marginRight: 3, fontSize: 10 }}>detail {d}</Tag>)}
      {it.count > 1 && <span style={{ fontSize: 10, color: "#9ca3af" }}>{it.count}×</span>}
    </div>
    : (it.count > 1 ? <div style={{ marginTop: 4, fontSize: 10, color: "#9ca3af" }}>{it.count} references</div> : null));

  const tagReason = (it) => (
    <div style={{ marginTop: 4 }}>
      {(it.tags || []).slice(0, 6).map((t) => (
        <Tag key={t} color="blue" style={{ marginRight: 3, fontSize: 10 }}>{t}</Tag>
      ))}
      {(it.tags || []).length > 6 && (
        <span style={{ fontSize: 10, color: "#9ca3af" }}>+{it.count - 6} more</span>
      )}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="94%"
      destroyOnClose
      bodyStyle={{ padding: 0, display: "flex", height: "100%", overflow: "hidden" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {trail.length > 0 && (
            <Button size="small" icon={<RollbackOutlined />} onClick={back}>
              Back to {trail[trail.length - 1].sheetNumber || "previous"}
            </Button>
          )}
          <span style={{ fontWeight: 600 }}>
            {current ? (current.sheetNumber ? `${current.sheetNumber} — ` : "") + (current.sheetName || "") : "Sheet"}
          </span>
        </div>
      }
    >
      {/* the canvas */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", padding: 10 }}>
        {pages.length
          ? <PageCanvas pages={pages} name={current.sheetName} projectId={current.projectId || (doc && doc.projectId)} docId={current._id} />
          : docsForPane.length
            ? <IssueDocumentPane docs={docsForPane} selectedUrl={docsForPane[0].url} fill />
            : <Empty description="This row has no file to draw" style={{ margin: "auto" }} />}
      </div>

      {/* everything it is connected to */}
      <div style={{
        width: PANEL_W, flexShrink: 0, borderLeft: "1px solid #e5e7eb",
        padding: "12px 14px", overflowY: "auto", background: "#fbfbfc",
      }}>
        {busy && <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>}
        {!busy && related && (
          <>
            <Group
              icon={<LinkOutlined style={{ color: "#2563eb" }} />}
              title="Refers to"
              hint="Callouts printed on this sheet."
              items={related.refersTo || []}
              onOpen={follow}
              reason={calloutReason}
              empty="This sheet calls out nothing."
            />
            <Group
              icon={<ApartmentOutlined style={{ color: "#7c3aed" }} />}
              title="Referred by"
              hint="Sheets that point AT this one — the direction you cannot see on paper."
              items={related.referredBy || []}
              onOpen={follow}
              reason={calloutReason}
              empty="Nothing in the set points here."
            />
            <Group
              icon={<TagsOutlined style={{ color: "#0891b2" }} />}
              title="Shares tags"
              hint="The same equipment appearing elsewhere, most in common first."
              items={related.sharesTags || []}
              onOpen={follow}
              reason={tagReason}
              empty="No tag on this sheet appears on another."
            />
            {!!(related.elements || []).length && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <BlockOutlined style={{ color: "#059669" }} />
                  <span style={{ fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    In the model
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280" }}>
                    {related.elements.length}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>
                  Elements carrying a tag from this sheet. Both worlds matched the token — that is
                  agreement, not proof they are the same object.
                </div>
                {related.elements.slice(0, 20).map((el) => (
                  <div key={el.uniqueId} style={{
                    border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 9px",
                    marginBottom: 6, background: "#fff", fontSize: 11,
                  }}>
                    <Tag color="cyan" style={{ fontSize: 10 }}>{el.tag}</Tag>
                    <div style={{ color: "#4b5563", marginTop: 3 }}>
                      {(el.name || el.uniqueId).slice(0, 70)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!!related.counts?.dangling && (
              <div style={{ fontSize: 11, color: "#d97706", marginTop: 10 }}>
                <WarningOutlined /> {related.counts.dangling} sheet
                {related.counts.dangling > 1 ? "s are" : " is"} called out but not in the register.
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
